import React from 'react';
import {ActivityIndicator, Pressable, ScrollView, useWindowDimensions, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {CourseSummary} from '../../../Hooks/LmsCourseApi/types';
import {useCourseList, useCreateCourse} from '../../../Hooks/LmsCourseApi/useCourseList';

import {formatDuration} from '../courseFormat';
import {describe} from '../courseStatus';
import CourseButton from './CourseButton';
import {CARD_SHADOW, contentWidth, F, MOBILE_BREAKPOINT} from '../courseTheme';

type Props = {
  onOpen: (uuid: string) => void;
  onCreate?: () => void;
  /** Open the review screen for a finished course. Admins only. */
  onReview?: (uuid: string) => void;
};

const CARD_MIN = 300;
const CARD_MAX = 380;

function Stat({value, label}: {value: string; label: string}) {
  return (
    <View style={{gap: 2}}>
      <CCText style={{fontSize: 18, fontWeight: '600', color: F.brownDark}}>
        {value}
      </CCText>
      <CCText style={{fontSize: 11, fontWeight: '600', color: F.mutedText, textTransform: 'uppercase', letterSpacing: 0.4}}>
        {label}
      </CCText>
    </View>
  );
}

function CourseCard({
  course,
  width,
  onPress,
  onRetry,
  retrying,
  onReview,
}: {
  course: CourseSummary;
  width: number;
  onPress: () => void;
  onRetry?: (course: CourseSummary, force: boolean) => void;
  retrying?: boolean;
  onReview?: (uuid: string) => void;
}) {
  const duration = formatDuration(course.duration_seconds);
  // Defaulted, not assumed present: an API that predates the status field
  // would otherwise crash the whole screen on `undefined.toUpperCase()`, and a
  // course that has a book is by definition finished.
  const status = course.status ?? (course.uuid ? 'done' : 'pending');
  // What we may honestly claim about this row, and what to offer about it.
  const verdict = describe(course);
  // A course still generating has no book to open yet, and a failed one has
  // nothing behind it — but both have to be visible, or the admin who started
  // the run has no way back to it and a dead run looks like nothing happened.
  const ready = status === 'done' && !!course.uuid;

  // The retry control is a sibling of the pressable region, not a child of
  // it. Nesting a button inside the card's Pressable needs the press kept
  // from bubbling up and opening the course, and every way of doing that —
  // claiming the responder, stopping propagation — swallowed the button's own
  // press instead, leaving a control that rendered and did nothing.
  return (
    <View
      style={[
        {
          width,
          backgroundColor: F.white,
          borderRadius: F.radius.card,
          overflow: 'hidden',
        },
        CARD_SHADOW,
      ]}>
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={ready ? `Open ${course.title}` : `${course.title} — ${status}`}
      accessibilityState={{disabled: !ready}}
      disabled={!ready}
      onPress={onPress}>
      {/* A generated course has no artwork, so the header is a coloured band
          carrying the one fact a learner decides on: how long it is. It uses
          the dark teal, not the page teal — matching the background makes the
          band read as floating off the card rather than topping it. */}
      <View
        style={{
          backgroundColor: F.tealDark,
          paddingHorizontal: 20,
          paddingVertical: 16,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}>
        <View
          style={{
            backgroundColor: ready ? 'rgba(255,255,255,0.22)' : F.wrongBg,
            borderRadius: 6,
            paddingHorizontal: 8,
            paddingVertical: 4,
          }}>
          <CCText
            style={{
              fontSize: 11,
              fontWeight: '600',
              color: ready ? F.white : F.wrongFg,
              letterSpacing: 0.4,
            }}>
            {ready ? 'MASTERCLASS' : verdict.label.toUpperCase()}
          </CCText>
        </View>
        {duration ? (
          <CCText style={{fontSize: 13, fontWeight: '600', color: F.white}}>
            {duration}
          </CCText>
        ) : null}
      </View>

      <View style={{padding: 20, gap: 16}}>
        <View style={{gap: 2}}>
          <CCText
            numberOfLines={2}
            style={{fontSize: 19, fontWeight: '600', color: F.brownDark, lineHeight: 26}}>
            {course.title}
          </CCText>
          {/* Who taught it is the actual basis for choosing between
              masterclasses, and the card was carrying it in the payload
              without ever showing it. */}
          {course.author ? (
            <CCText style={{fontSize: 14, fontWeight: '500', color: F.brown}}>
              {course.author}
            </CCText>
          ) : null}
        </View>

        <View style={{flexDirection: 'row', gap: 28}}>
          <Stat value={String(course.chapter_count)} label="Chapters" />
          <Stat value={String(course.puzzle_count)} label="Puzzles" />
        </View>

        {/* The whole card is the control, so this is deliberately a label and
            not a nested button — it used to look pressable, give no press
            feedback, and be invisible to assistive tech. */}
        {ready ? (
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
            <CCText
              style={{
                fontSize: 14,
                fontWeight: '600',
                color: F.tealDark,
                letterSpacing: 0.2,
              }}>
              Start course
            </CCText>
            <Ionicons name="arrow-forward" size={15} color={F.tealDark} />
          </View>
        ) : (
          <CCText
            style={{
              fontSize: 13,
              fontWeight: '500',
              // A stalled run is not neutral information — it is something
              // that needs a person, and grey is how it gets scrolled past.
              color: verdict.stalled ? F.wrongFg : F.mutedText,
              lineHeight: 19,
            }}>
            {verdict.detail}
          </CCText>
        )}

      </View>
    </Pressable>

      {/* Outside the Pressable above, so its press is its own. Offered on a
          finished course too: recordings get re-cut and the detector
          improves, and there was previously no way to rebuild one at all
          without going through the S3 link form. */}
      {(onRetry && verdict.action !== 'none') || (onReview && ready) ? (
        <View style={{paddingHorizontal: 20, paddingBottom: 20, flexDirection: 'row', gap: 8, flexWrap: 'wrap'}}>
          {onReview && ready && course.uuid ? (
            <CourseButton
              label="Review"
              onPress={() => onReview(course.uuid as string)}
            />
          ) : null}
          {onRetry && verdict.action !== 'none' ? (
            <CourseButton
              label={retrying ? 'Starting…' : verdict.actionLabel}
              variant="review"
              disabled={!!retrying}
              onPress={() => onRetry(course, verdict.forces)}
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

/**
 * Every generated course, as a card grid.
 *
 * Cards are a fixed width rather than full-bleed: one course stretched across
 * a 1300px row reads as a broken layout, and the eye has nothing to anchor on.
 */
export default function CourseListScreen({onOpen, onCreate, onReview}: Props) {
  const {data, isLoading, error, refetch} = useCourseList();
  const {width: windowWidth} = useWindowDimensions();
  const courses = data?.courses ?? [];
  // The server decides who may generate a course; this only decides whether to
  // offer the button, so it does not become a second, drifting copy of the
  // rule. The POST re-checks.
  const canCreate = !!data?.can_create;

  const create = useCreateCourse();
  const [retryingId, setRetryingId] = React.useState<number | null>(null);
  const [retryError, setRetryError] = React.useState<string | null>(null);

  /**
   * Start a run again from the card it belongs to.
   *
   * Whether to force is the verdict's call, not this component's. The server
   * only re-dispatches an unfinished job that is `pending` *and* stale —
   * `_is_stale_pending` never expires a `running` row — so a stalled run
   * without force comes back as a 200 carrying the same row, and the button
   * silently does nothing.
   */
  const retry = React.useCallback(
    (course: CourseSummary, force: boolean) => {
      setRetryingId(course.video_course_id);
      setRetryError(null);
      create.mutate(
        {video_course_id: course.video_course_id, force},
        {
          onSuccess: () => {
            setRetryingId(null);
            refetch();
          },
          onError: (err: any) => {
            setRetryingId(null);
            setRetryError(
              err?.response?.data?.error ??
                'Could not start that again. Try once more in a moment.',
            );
          },
        },
      );
    },
    [create, refetch],
  );

  // Leave room for the app's own left nav before working out how many fit.
  const available = Math.max(CARD_MIN, contentWidth(windowWidth) - 48);
  const narrow = available < MOBILE_BREAKPOINT;
  const perRow = Math.max(1, Math.floor(available / CARD_MIN));
  const cardWidth = narrow
    ? Math.min(available, CARD_MAX)
    : Math.min(CARD_MAX, Math.floor((available - (perRow - 1) * 20) / perRow));

  return (
    <View style={{flex: 1, backgroundColor: F.teal}}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          paddingHorizontal: 24,
          paddingTop: 28,
          paddingBottom: 4,
          gap: 12,
          flexWrap: 'wrap',
        }}>
        <View style={{gap: 4}}>
          {/* "Masterclass", matching the drawer entry. "Courses" is a
              different screen in the same menu (the School of Chess LMS), and
              two headings with one name is how you get a support ticket. */}
          <CCText style={{fontSize: 28, fontWeight: '600', color: F.white}}>
            Masterclass
          </CCText>
          <CCText style={{fontSize: 14, fontWeight: '500', color: F.white}}>
            {courses.length
              ? `${courses.length} course${courses.length === 1 ? '' : 's'} built from masterclass recordings`
              : 'Built automatically from masterclass recordings'}
          </CCText>
        </View>
        {canCreate && onCreate ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Create course"
            onPress={onCreate}
            style={{
              backgroundColor: F.yellow,
              borderBottomWidth: 4,
              borderBottomColor: F.yellowBorder,
              borderRadius: F.radius.button,
              paddingHorizontal: 20,
              paddingVertical: 10,
            }}>
            <CCText
              style={{
                fontSize: 13,
                fontWeight: '600',
                color: F.brown,
                textTransform: 'uppercase',
                letterSpacing: 0.32,
              }}>
              + Create course
            </CCText>
          </Pressable>
        ) : null}
      </View>

      <ScrollView contentContainerStyle={{padding: 24, paddingTop: 20}}>
        {isLoading ? <ActivityIndicator color={F.white} /> : null}

        {error ? (
          <View style={{gap: 12, alignItems: 'flex-start'}}>
            <CCText style={{color: F.white, fontSize: 15, fontWeight: '600'}}>
              Could not load courses.
            </CCText>
            {/* Without this the only way out of a failed fetch is reloading
                the browser. */}
            <CourseButton label="Try again" onPress={() => refetch()} />
          </View>
        ) : null}

        {!isLoading && !error && courses.length === 0 ? (
          <View
            style={[
              {
                backgroundColor: F.white,
                borderRadius: F.radius.card,
                padding: 28,
                maxWidth: 520,
                gap: 8,
              },
              CARD_SHADOW,
            ]}>
            <CCText style={{fontSize: 18, fontWeight: '600', color: F.brownDark}}>
              No courses yet
            </CCText>
            <CCText style={{fontSize: 14, fontWeight: '500', color: F.brown, lineHeight: 22}}>
              Generate one from a masterclass recording and it will appear here.
            </CCText>
          </View>
        ) : null}

        {retryError ? (
          <CCText
            style={{color: F.white, fontSize: 14, fontWeight: '600', marginBottom: 12}}>
            {retryError}
          </CCText>
        ) : null}

        <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 20}}>
          {courses.map(course => (
            <CourseCard
              // Keyed by the recording, not the book uuid: a course that has
              // not finished generating has no uuid yet, so two in-flight runs
              // would collide on a null key.
              key={course.video_course_id}
              course={course}
              width={cardWidth}
              onPress={() => course.uuid && onOpen(course.uuid)}
              onRetry={canCreate ? retry : undefined}
              retrying={retryingId === course.video_course_id}
              onReview={canCreate ? onReview : undefined}
            />
          ))}
        </View>
      </ScrollView>
    </View>
  );
}
